/** Renders a booking batch-edit task result: outcome counts, the caches & syncs
 *  that were (or would be) flushed, links to the run's audit trail / change
 *  history / deliveries, and a bounded per-booking table (changes, skipped
 *  blocks, warnings, events). Used inline after a dry run and as the Background
 *  Tasks result panel.
 */
import LaunchIcon from '@mui/icons-material/Launch';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type {
  BookingEditBlockResult,
  BookingEditOutcome,
  BookingEditRecord,
  BookingEditTaskResult,
} from '@/api/bookingEdit';
import {
  BLOCK_OUTCOME_LABELS,
  EVENT_STATUS_LABELS,
  OUTCOME_LABELS,
  OUTCOME_SEVERITY,
  deliveryLogsHrefForTag,
} from '@/api/bookingEdit';

const MAX_ROWS_SHOWN = 100;

function fmt(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    const o = value as { name?: string; id?: number };
    if (o.name != null || o.id != null) return `${o.name ?? ''}${o.id != null ? ` #${o.id}` : ''}`.trim();
    return JSON.stringify(value);
  }
  const s = String(value);
  return s.length > 40 ? `${s.slice(0, 40)}…` : s;
}

function changeText(b: BookingEditBlockResult): string {
  if (b.outcome === 'change') return `${b.field}: ${fmt(b.from)} → ${fmt(b.to)}${b.note ? ` (${b.note})` : ''}`;
  return `${b.field}: ${BLOCK_OUTCOME_LABELS[b.outcome] ?? b.outcome}${b.note ? ` — ${b.note}` : ''}`;
}

function rowSummary(rec: BookingEditRecord): string {
  if (rec.detail) return rec.detail;
  const parts = rec.blocks.filter((b) => b.outcome !== 'no_change').map(changeText);
  if (rec.tags_removed?.length) parts.push(`tags removed: ${rec.tags_removed.join(', ')}`);
  return parts.join(' · ');
}

export function BookingEditReport({ result, taskId }: { result: BookingEditTaskResult; taskId: string }) {
  const rows = result.records.slice(0, MAX_ROWS_SHOWN);
  const effects = Object.entries(result.effects ?? {});
  const flushed = effects.filter(([, e]) => e.kind === 'flushed');
  const automatic = effects.filter(([, e]) => e.kind === 'automatic');
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {Object.entries(result.counts).map(([code, n]) => (
          <Chip
            key={code}
            size="small"
            variant="outlined"
            color={OUTCOME_SEVERITY[code as BookingEditOutcome] ?? 'default'}
            label={`${OUTCOME_LABELS[code as BookingEditOutcome] ?? code}: ${(n ?? 0).toLocaleString()}`}
          />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Link component={RouterLink} to={`/bgtasks/${taskId}`} variant="caption" sx={linkSx}>
          Full report <LaunchIcon sx={{ fontSize: 14 }} />
        </Link>
        <Link component={RouterLink} to={`/audit?task_id=${taskId}`} variant="caption" sx={linkSx}>
          Audit trail <LaunchIcon sx={{ fontSize: 14 }} />
        </Link>
        {!result.dry_run && (
          <>
            <Link component={RouterLink} to={`/changes?task_id=${taskId}`} variant="caption" sx={linkSx}>
              Change history <LaunchIcon sx={{ fontSize: 14 }} />
            </Link>
            <Link component={RouterLink} to={deliveryLogsHrefForTag(result.run_tag)} variant="caption" sx={linkSx}>
              Deliveries <LaunchIcon sx={{ fontSize: 14 }} />
            </Link>
          </>
        )}
      </Stack>

      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" component="div">
          {result.dry_run ? 'Caches & syncs this run would trigger:' : 'Caches & syncs flushed:'}
          {flushed.length === 0 && ' none'}
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          {flushed.map(([code, e]) => {
            const f = result.flush?.[code];
            const keys = (e.keys ?? []).map((k) => JSON.stringify(k)).join('\n');
            return (
              <Tooltip key={code} title={<Box sx={{ whiteSpace: 'pre-wrap' }}>{keys || e.label}</Box>}>
                <Chip
                  size="small"
                  color={f && f.failed > 0 ? 'error' : 'default'}
                  label={`${e.label}: ${e.count.toLocaleString()}${f && f.failed > 0 ? ` (${f.failed} failed)` : ''}`}
                />
              </Tooltip>
            );
          })}
          {automatic.map(([code, e]) => (
            <Chip key={code} size="small" variant="outlined" label={`${e.label} (automatic)`} />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          Events are tagged <code>{result.run_tag}</code>.
          {result.history_failures ? ` Change history could not be written for ${result.history_failures} booking(s).` : ''}
          {result.stopped_early ? ' The run was stopped early.' : ''}
        </Typography>
      </Box>

      <Box sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 150 }}>Booking</TableCell>
              <TableCell sx={{ width: 90 }}>Tenant</TableCell>
              <TableCell sx={{ width: 150 }}>Outcome</TableCell>
              <TableCell>Changes</TableCell>
              <TableCell sx={{ width: 170 }}>Events</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((rec) => (
              <TableRow key={rec.id} hover>
                <TableCell>
                  <Link component={RouterLink} to={`/bookings/${rec.id}`} target="_blank" rel="noopener">
                    {rec.custom_id ?? `#${rec.id}`}
                  </Link>
                  <Typography variant="caption" color="text.secondary" component="div">
                    #{rec.id}
                    {rec.via_lead_of ? ` · via lead #${rec.via_lead_of}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>{rec.tenant?.code ?? '—'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      size="small"
                      variant="outlined"
                      color={OUTCOME_SEVERITY[rec.outcome] ?? 'default'}
                      label={OUTCOME_LABELS[rec.outcome] ?? rec.outcome}
                    />
                    {rec.warnings.length > 0 && (
                      <Tooltip title={<Box sx={{ whiteSpace: 'pre-wrap' }}>{rec.warnings.join('\n')}</Box>}>
                        <WarningAmberIcon fontSize="small" color="warning" aria-label="warnings" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Tooltip title={<Box sx={{ whiteSpace: 'pre-wrap' }}>{rec.blocks.map(changeText).join('\n')}</Box>}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 420 }}>
                      {rowSummary(rec) || '—'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {rec.events.length === 0 ? (
                    '—'
                  ) : (
                    <Typography variant="caption" component="div">
                      {rec.events.map((e, i) => (
                        <div key={i}>
                          {e.code.split('.').pop()}: {EVENT_STATUS_LABELS[e.status] ?? e.status}
                        </div>
                      ))}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {(result.records.length > MAX_ROWS_SHOWN || result.records_truncated) && (
        <Typography variant="caption" color="text.secondary">
          Showing the first {rows.length.toLocaleString()} of {result.total.toLocaleString()} booking(s) — the task
          holds the full report.
        </Typography>
      )}
    </Box>
  );
}

const linkSx = { display: 'inline-flex', alignItems: 'center', gap: 0.25 } as const;
